import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  fetchCarGenerationOptions,
  fetchCarMarkOptions,
  fetchCarModelOptions,
  fetchCarModificationOptions,
} from '@/shared/api/car';
import { ApiError } from '@/shared/api/submit';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { SaveBar } from '@/shared/ui/SaveBar';
import { TextField } from '@/shared/ui/TextField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import {
  fetchAiRequest,
  fetchNormHours,
  saveAiRequest,
  type AiRequest,
  type AiRequestValues,
} from './api';
import { ServiceTreeField } from './ServiceTreeField';

const MAX_VARIANT_LENGTH = 500;
const SELECT_WIDTH = 400;
const HOURS_DECIMALS = 2;

const formatHours = (hours: number) => hours.toFixed(HOURS_DECIMALS).replace('.', ',');

const validate = (values: AiRequestValues) => {
  const missing =
    (!values.markId && 'Не выбрана марка автомобиля') ||
    (!values.modelId && 'Не выбрана модель автомобиля') ||
    (!values.generationId && 'Не выбрано поколение автомобиля') ||
    (!values.modificationId && 'Не выбрана модификация автомобиля') ||
    (values.serviceIds.length === 0 && 'Должна быть выбрана как минимум одна услуга');
  if (missing) throw new ApiError({ common: [missing] });
};

type CarFieldKey = 'markId' | 'modelId' | 'generationId' | 'modificationId';

/** Each car field resets every field below it. */
const CAR_FIELDS: { key: CarFieldKey; label: string }[] = [
  { key: 'markId', label: 'Марка автомобиля' },
  { key: 'modelId', label: 'Модель автомобиля' },
  { key: 'generationId', label: 'Поколение автомобиля' },
  { key: 'modificationId', label: 'Модификация автомобиля' },
];

const AIRequestForm = ({ request, initial }: { request: AiRequest; initial: AiRequestValues }) => {
  const form = useEntityForm(initial, (values) => {
    validate(values);
    return saveAiRequest(request.id, values);
  });
  const { values, errors, setField } = form;

  const marks = useQuery({ queryKey: ['car-mark-options', ''], queryFn: () => fetchCarMarkOptions('') });
  const models = useQuery({
    queryKey: ['car-model-options', values.markId],
    queryFn: () => fetchCarModelOptions(values.markId),
    enabled: Boolean(values.markId),
  });
  const generations = useQuery({
    queryKey: ['car-generation-options', values.modelId],
    queryFn: () => fetchCarGenerationOptions(values.modelId),
    enabled: Boolean(values.modelId),
  });
  const modifications = useQuery({
    queryKey: ['car-modification-options', values.generationId],
    queryFn: () => fetchCarModificationOptions(values.generationId),
    enabled: Boolean(values.generationId),
  });

  const canCalcHours = Boolean(values.modificationId && values.generationId);
  const normHours = useQuery({
    queryKey: ['ai-norm-hours', values.modificationId, values.generationId, [...values.serviceIds].sort()],
    queryFn: () => fetchNormHours(values.serviceIds, values.modificationId, values.generationId),
    enabled: canCalcHours,
    placeholderData: keepPreviousData,
  });

  const carQueries = { markId: marks, modelId: models, generationId: generations, modificationId: modifications };
  const options = (key: CarFieldKey): SelectOption[] => carQueries[key].data ?? [];

  const changeCarField = (index: number, value: string) => {
    CAR_FIELDS.forEach(({ key }, i) => {
      if (i === index) setField(key, value);
      else if (i > index) setField(key, '');
    });
  };

  const setVariant = (index: number, text: string) =>
    setField('variantsMessage', values.variantsMessage.map((item, i) => (i === index ? text : item)));

  return (
    <>
      <TextField label="ID" readOnly value={String(request.id)} onChange={() => undefined} />
      <TextField label="Запрос от пользователя в AI" multiline readOnly value={request.message} onChange={() => undefined} />

      <FormField label="Варианты запроса в AI" errors={errors.additional_queries}>
        {values.variantsMessage.map((variant, index) => (
          <div key={index} className="input-group" style={{ marginBottom: 10 }}>
            <textarea
              className="form-control"
              maxLength={MAX_VARIANT_LENGTH}
              value={variant}
              onChange={(e) => setVariant(index, e.target.value)}
            />
            <span className="input-group-btn">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setField('variantsMessage', values.variantsMessage.filter((_, i) => i !== index))}
              >
                Удалить
              </button>
            </span>
          </div>
        ))}
        <div>
          <button
            type="button"
            className="btn btn-info"
            onClick={() => setField('variantsMessage', [...values.variantsMessage, ''])}
          >
            Добавить вариант запроса в AI
          </button>
        </div>
      </FormField>

      {CAR_FIELDS.map(({ key, label }, index) => (
        <FormField key={key} label={label} errors={key === 'modificationId' ? errors.id_modification : errors[key]}>
          <FilterSelect
            options={options(key)}
            value={values[key]}
            width={SELECT_WIDTH}
            isLoading={carQueries[key].isFetching}
            isDisabled={index > 0 && !values[CAR_FIELDS[index - 1].key]}
            onChange={(value) => changeCarField(index, value)}
          />
        </FormField>
      ))}

      <ServiceTreeField
        selectedIds={values.serviceIds}
        errors={errors.service_catalog}
        onChange={(ids) => setField('serviceIds', ids, 'service_catalog')}
      />

      <TextField
        label="Нормочасы на услуги"
        readOnly
        maxWidth={SELECT_WIDTH}
        value={formatHours(canCalcHours ? (normHours.data ?? request.normHours) : 0)}
        onChange={() => undefined}
      />

      <SaveBar isSaving={form.isSaving} saved={form.saved} errors={errors} onSave={form.save} />
    </>
  );
};

export const AIRequestPage = () => {
  const id = useRouteId();
  const query = useQuery({ queryKey: ['ai-request', id], queryFn: () => fetchAiRequest(id) });

  return (
    <DetailLayout title="Запрос AI" isLoading={query.isLoading} error={query.error}>
      <p>
        <Link to="/stat/ai-queries">⇐ Вернуться к запросам</Link>
      </p>
      {query.data && <AIRequestForm key={id} request={query.data.request} initial={query.data.values} />}
    </DetailLayout>
  );
};
